import { Component } from 'react';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({
      error,
      errorInfo
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-page-bg px-4">
          <div className="max-w-md w-full">
            <div className="card text-center">
              {/* Error Icon */}
              <div className="mx-auto h-16 w-16 text-red-500 mb-4">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
                  />
                </svg>
              </div>

              {/* Error Message */}
              <h1 className="text-2xl font-bold text-primary-text mb-2">
                Oops! Something went wrong
              </h1>
              <p className="text-secondary-text mb-6">
                We encountered an unexpected error. Don't worry, we've logged it and will look into it.
              </p>

              {/* Error Details (Development Only) */}
              {import.meta.env.DEV && this.state.error && (
                <div className="mb-6 text-left">
                  <details className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
                    <summary className="cursor-pointer text-sm font-medium text-red-600 dark:text-red-400 mb-2">
                      Error Details (Dev Only)
                    </summary>
                    <div className="text-xs text-red-800 dark:text-red-300 space-y-2">
                      <div>
                        <strong>Error:</strong>
                        <pre className="mt-1 overflow-auto">{this.state.error.toString()}</pre>
                      </div>
                      {this.state.errorInfo && (
                        <div>
                          <strong>Component Stack:</strong>
                          <pre className="mt-1 overflow-auto">{this.state.errorInfo.componentStack}</pre>
                        </div>
                      )}
                    </div>
                  </details>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => window.location.reload()}
                  className="btn-primary"
                >
                  Reload Page
                </button>
                <button
                  onClick={this.handleReset}
                  className="btn-secondary"
                >
                  Go to Home
                </button>
              </div>

              {/* Support Link */}
              <p className="mt-6 text-xs text-secondary-text">
                If this problem persists, please{' '}
                <a href="/contact" className="text-primary hover:underline">
                  contact support
                </a>
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
